import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  Icon,
  Grid,
  TextField,
  InputAdornment,
  Modal,
  IconButton,
  TablePagination,
  Select,
  MenuItem,
} from '@mui/material';
import orderBy from 'lodash/orderBy';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import SpinningSpark from '../shared/components/SpinningSpark';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import PhotoReviewCard from './PhotoReviewCard';
import StorePhotosReviewApi from './StorePhotosReview.api';

export default function StorePhotosReview() {
  const { showAlert } = useContext(AlertsContext);

  const [storePhotos, setStorePhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('storeNumber');

  const styles = {
    container: {
      marginBottom: '3vh',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    searchInput: {
      width: '320px',
      height: '30px',
      margin: '30px 0',
      backgroundColor: '#FF',
      '& .MuiOutlinedInput-root': {
        padding: '0 20px',
        '& fieldset': { borderColor: '#DADADA' },
        '&:hover fieldset': { borderColor: '#DADADA' },
        '&.Mui-focused fieldset': { borderColor: '#DADADA' },
        borderRadius: '100px',
      },
      '& .MuiInputBase-input': {
        padding: '12px 0',
        boxSizing: 'border-box',
        height: '100%',
      },
      '& .MuiInputBase-input::placeholder': {
        color: '#575757',
        fontWeight: 500,
      },
    },
    modalContainer: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '90vw',
      maxWidth: '1030px',
      height: '90vh',
      maxHeight: '800px',
      backgroundColor: 'black',
      boxShadow: 24,
      outline: 'none',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalImage: {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      color: 'white',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      '&:hover': { backgroundColor: 'black' },
      zIndex: 1,
    },
  };

  useEffect(() => {
    fetchStorePhotos();
  }, []);

  const fetchStorePhotos = async () => {
    setLoading(true);
    try {
      const response = await StorePhotosReviewApi.getStorePhotos();

      // Ensure the response is an array before setting the state
      if (Array.isArray(response)) {
        setStorePhotos(response);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      showAlert('Failed to fetch store photos', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterPhotosByStoreNumber = (photos, query) => {
    const normalized = query.trim();
    if (!normalized) return photos;

    // If query is not a valid number, return nothing
    const queryAsNumber = Number(normalized);
    if (Number.isNaN(queryAsNumber)) return [];

    return photos.filter((photo) => photo.store_number === queryAsNumber);
  };

  useEffect(() => {
    let result = filterPhotosByStoreNumber(storePhotos, searchQuery);
    result = sortStorePhotosReview(result, sortBy);
    setFilteredPhotos(result);
    setPage(0); // Reset to page 0 on search
  }, [searchQuery, storePhotos, sortBy]);

  const paginatedPhotos = useMemo(() => {
    return filteredPhotos.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  }, [filteredPhotos, page, itemsPerPage]);

  const handleCardClick = (photo) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
  };

  const renderPosts = () => {
    if (loading) return <SpinningSparkBox />;

    return (
      <>
        <Box
          display="flex"
          justifyContent="space-between"
          mb={2}
          alignItems="center"
        >
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Store Number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#0171CC' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery ? (
                    <Icon
                      onClick={() => setSearchQuery('')}
                      style={{ cursor: 'pointer', color: '#AEBDCC' }}
                    >
                      <CloseIcon />
                    </Icon>
                  ) : (
                    <span style={{ width: '24px' }} /> // Placeholder to maintain width
                  )}
                </InputAdornment>
              ),
            }}
            sx={styles.searchInput}
          />
          <TablePagination
            component="div"
            count={filteredPhotos.length}
            page={page}
            onPageChange={(_, val) => setPage(val)}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={(event) => {
              setItemsPerPage(parseInt(event.target.value, 10));
              setPage(0); // Reset to page 0 on items per page change
            }}
            sx={{ color: '#5C6A7D' }}
          />
        </Box>
        {filteredPhotos.length > 0 ? (
          <Grid container spacing={2}>
            {paginatedPhotos.map((photo) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={`${photo.store_number}-${photo.requirement_id}`}
              >
                <PhotoReviewCard
                  photo={photo}
                  allowActions
                  onClick={() => handleCardClick(photo)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box style={{ height: '100vh' }}>
            <Typography variant="h6" align="center" sx={{ mt: 10 }}>
              No photos available
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={styles.container}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Store Photos Review</Typography>
        <Box display="flex" alignItems="center" ml={2}>
          <Typography variant="body2">Sort by</Typography>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            size="small"
            sx={{ minWidth: 160, ml: 2, borderRadius: '15px' }}
          >
            <MenuItem value="storeNumber">Store Number</MenuItem>
            <MenuItem value="oldest">Oldest first</MenuItem>
            <MenuItem value="newest">Newest first</MenuItem>
          </Select>
        </Box>
      </Box>
      {renderPosts()}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={styles.modalContainer}>
          <IconButton
            onClick={() => setIsModalOpen(false)}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          {selectedPhoto && (
            <img
              src={selectedPhoto.photo_url}
              alt={`Store ${selectedPhoto.requirement_title}`}
              style={styles.modalImage}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}

// Spinner component
function SpinningSparkBox() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="40vh"
    >
      <SpinningSpark />
    </Box>
  );
}

// Function to sort photos based on the selected criteria
function sortStorePhotosReview(photos, sortBy) {
  let sortFields = [];
  let sortOrders = [];

  switch (sortBy) {
    case 'oldest':
      sortFields = ['updated_at'];
      sortOrders = ['asc'];
      break;
    case 'newest':
      sortFields = ['updated_at'];
      sortOrders = ['desc'];
      break;
    case 'storeNumber':
    default:
      sortFields = [
        'store_number',
        'department_title',
        'area_title',
        'requirement_title',
      ];
      sortOrders = ['asc', 'asc', 'asc', 'asc'];
      break;
  }

  return orderBy(photos, sortFields, sortOrders);
}
